import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { fetchLoanDocuments, rerunEvalSlice } from "./api";
import { RoleLabel } from "./CompareBits";
import DocSelectionDiff from "./DocSelectionDiff";
import { STAGE_LABELS, STAGE_ORDER, formatMoney } from "./evalOutput";
import Step1Diff from "./Step1Diff";
import Step3Diff from "./Step3Diff";

const PIPELINE_STEPS = ["Doc selection", "Step 1", "Step 3"];

const SeverityChip = ({ severity }) => {
  if (severity == null) {
    return <span className="mse-sev mse-sev--na">n/a</span>;
  }

  return <span className={`mse-sev mse-sev--${severity}`}>{severity}</span>;
};

function getStep3ChainState(caseRow) {
  if (
    caseRow.replayMode === "production_flow" &&
    caseRow.chain?.connected === true
  ) {
    return {
      isConnected: true,
      title: "Step 3 used this pipeline's own Step 1",
      detail:
        "The Eval model's Step 3 was rendered from the Eval model's Step 1 result. No prompt substitution and no baseline context were involved.",
    };
  }
  if (caseRow.replayMode === "chained" && caseRow.chain?.substituted === true) {
    return {
      isConnected: true,
      title: "Step 3 used this pipeline's own Step 1",
      detail: "Eval Step 3 consumed this run's Eval Step 1 output.",
    };
  }
  if (caseRow.replayMode === "chained") {
    return {
      isConnected: false,
      title: "Step 3 could not use Eval Step 1",
      detail: `Step 3 fell back: ${
        caseRow.chain?.substitutionReason || "unknown reason"
      }.`,
    };
  }

  return {
    isConnected: false,
    title: "Legacy isolated replay",
    detail:
      "Eval Step 3 reused Base context, so this pane cannot support an end-to-end model-switch conclusion.",
  };
}

/**
 * States plainly that the two runs are separate, because the whole report is
 * only trustworthy if each model ran the entire checklist on its own output.
 */
function PipelinePanel({ baseModel, evalModel }) {
  return (
    <section className="mse-pipelines">
      <div className="mse-pipelines__head">
        <strong>Two independent pipelines</strong>
        <span className="mse-muted-xs">
          Each model ran the full production checklist end to end on its own
          output. Nothing was written to the loan.
        </span>
      </div>
      <div className="mse-pipelines__grid">
        {[
          { role: "base", model: baseModel },
          { role: "eval", model: evalModel },
        ].map((pipeline) => (
          <div
            key={pipeline.role}
            className={`mse-pipeline mse-pipeline--${pipeline.role}`}
          >
            <RoleLabel role={pipeline.role} model={pipeline.model} />
            <ol className="mse-pipeline__steps">
              {PIPELINE_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  );
}

function StageSection({
  caseRow,
  docsById,
  docsLoading,
  onRerun,
  rerunningStage,
  baseModel,
  evalModel,
}) {
  const stageId = caseRow.stageId;
  const fidelityNotes = caseRow.candidate?.fidelityNotes || [];
  const step3ChainState =
    stageId === "pcStep3" ? getStep3ChainState(caseRow) : null;
  const stageProps = { caseRow, baseModel, evalModel };

  return (
    <section className="mse-stage-section" id={`stage-${stageId}`}>
      <div className="mse-stage-section__header">
        <div>
          <h3>
            {STAGE_LABELS[stageId] || caseRow.stageLabel || stageId}{" "}
            <SeverityChip
              severity={caseRow.diff?.severity || caseRow.severity}
            />
          </h3>
          {caseRow.langfuseUrl && (
            <a
              href={caseRow.langfuseUrl}
              target="_blank"
              rel="noreferrer"
              className="mse-link"
            >
              Langfuse trace
            </a>
          )}
        </div>
        <button
          type="button"
          className="mse-btn"
          disabled={rerunningStage === stageId}
          onClick={() => onRerun(stageId)}
        >
          {rerunningStage === stageId ? "Queuing…" : "Re-run this loan"}
        </button>
      </div>

      {fidelityNotes.length > 0 && (
        <p className="mse-fidelity">{fidelityNotes.join(" · ")}</p>
      )}

      {stageId === "pcStep3" && (
        <div
          className={`mse-chain-banner ${
            step3ChainState.isConnected
              ? "mse-chain-banner--connected"
              : "mse-chain-banner--isolated"
          }`}
        >
          <strong>{step3ChainState.title}</strong>
          <span>{step3ChainState.detail}</span>
        </div>
      )}

      {caseRow.error && (
        <div className="mse-error-card">Replay error: {caseRow.error}</div>
      )}

      {!caseRow.error && stageId === "docSelection" && (
        <DocSelectionDiff
          {...stageProps}
          docsById={docsById}
          docsLoading={docsLoading}
        />
      )}
      {!caseRow.error && stageId === "pcStep1" && <Step1Diff {...stageProps} />}
      {!caseRow.error && stageId === "pcStep3" && <Step3Diff {...stageProps} />}
      {!caseRow.error &&
        !["docSelection", "pcStep1", "pcStep3"].includes(stageId) && (
          <p className="mse-muted">
            No structured diff viewer for this stage yet. Severity:{" "}
            {caseRow.diff?.severity || caseRow.severity || "—"}.
          </p>
        )}
    </section>
  );
}

export default function LoanDriftDetail({
  runId,
  env,
  loanGroup,
  cases = [],
  config,
  onBack,
  onChildRunQueued,
}) {
  const [docsById, setDocsById] = useState({});
  const [docsLoading, setDocsLoading] = useState(false);
  const [rerunningStage, setRerunningStage] = useState(null);

  const loanCases = useMemo(() => {
    const fromGroup = Array.isArray(loanGroup?.cases) ? loanGroup.cases : [];
    const byTrace = new Map(
      cases
        .filter((row) => row.loanId === loanGroup?.loanId)
        .map((row) => [row.traceId || `${row.stageId}-${row.taskId}`, row]),
    );

    // Prefer full case bodies from report.cases; fall back to loanGroup stubs.
    const merged = STAGE_ORDER.map((stageId) => {
      const full = cases.find(
        (row) => row.loanId === loanGroup?.loanId && row.stageId === stageId,
      );
      if (full) {
        return full;
      }
      const stub = fromGroup.find((row) => row.stageId === stageId);

      return stub || null;
    }).filter(Boolean);

    if (merged.length > 0) {
      return merged;
    }

    return Array.from(byTrace.values());
  }, [cases, loanGroup]);

  const baseModel = config?.baselineModel || "production default";
  const evalModel =
    config?.candidateModel ||
    loanCases.find((row) => row.candidate?.candidateModel)?.candidate
      ?.candidateModel ||
    "candidate";

  useEffect(() => {
    if (!loanGroup?.organizationId || !loanGroup?.loanId) {
      return undefined;
    }
    let cancelled = false;
    setDocsLoading(true);
    fetchLoanDocuments(
      {
        organizationId: loanGroup.organizationId,
        loanId: loanGroup.loanId,
      },
      env,
    )
      .then((res) => {
        if (cancelled) {
          return;
        }
        const map = {};
        (res.documents || []).forEach((doc) => {
          map[doc.id] = doc;
        });
        setDocsById(map);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.message || "Could not load documents.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDocsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [env, loanGroup?.loanId, loanGroup?.organizationId]);

  const onRerun = async (stageId) => {
    setRerunningStage(stageId);
    try {
      const result = await rerunEvalSlice(
        runId,
        {
          loanIds: [loanGroup.loanId],
          stages: ["docSelection", "pcStep1", "pcStep3"],
        },
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not queue re-run.");
      }
      toast.success(
        `Queued child run ${result.runId}. Original run is unchanged.`,
      );
      onChildRunQueued?.(result.runId);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRerunningStage(null);
    }
  };

  if (!loanGroup) {
    return null;
  }

  const headline = loanGroup.headline || {};

  return (
    <div className="mse-loan-detail">
      <button type="button" className="mse-btn mse-back" onClick={onBack}>
        ← Back to loan table
      </button>

      <header className="mse-loan-detail__header">
        <div>
          <h2 className="mse-run-detail__title">
            {loanGroup.borrower || "Borrower unavailable"}{" "}
            <SeverityChip severity={loanGroup.highestSeverity} />
          </h2>
          <p className="mse-subtitle">
            {loanGroup.loanType || "Loan type n/a"} ·{" "}
            <code className="mse-mono">{loanGroup.loanId}</code>
            {loanGroup.loanUrl && (
              <>
                {" · "}
                <a
                  href={loanGroup.loanUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mse-link"
                >
                  Open loan
                </a>
              </>
            )}
          </p>
          <p className="mse-muted">
            Human reference income:{" "}
            {formatMoney(loanGroup.humanCombinedMonthlyIncome)}
          </p>
        </div>
        <div className="mse-loan-detail__chips">
          {STAGE_ORDER.map((stageId) => (
            <span key={stageId} className="mse-stage-chip">
              {STAGE_LABELS[stageId]}{" "}
              <SeverityChip
                severity={loanGroup.stageSeverity?.[stageId] ?? null}
              />
            </span>
          ))}
        </div>
      </header>

      <PipelinePanel baseModel={baseModel} evalModel={evalModel} />

      {(headline.status || headline.qualifyingMonthlyIncome) && (
        <div className="mse-loan-headline">
          {headline.status && (
            <div>
              <span className="mse-stat-label">Status</span>
              {headline.status.baseline || "—"} →{" "}
              {headline.status.candidate || "—"}
              {headline.status.changed ? " (changed)" : ""}
            </div>
          )}
          {headline.qualifyingMonthlyIncome && (
            <div>
              <span className="mse-stat-label">Income</span>
              {formatMoney(headline.qualifyingMonthlyIncome.baseline)} →{" "}
              {formatMoney(headline.qualifyingMonthlyIncome.candidate)}
            </div>
          )}
          {headline.docSelection && (
            <div>
              <span className="mse-stat-label">Documents</span>
              Base {headline.docSelection.baselineCount} · Eval{" "}
              {headline.docSelection.candidateCount}
              {headline.docSelection.jaccard == null
                ? ""
                : ` · ${(headline.docSelection.jaccard * 100).toFixed(0)}% overlap`}
            </div>
          )}
        </div>
      )}

      {loanCases.map((caseRow) => (
        <StageSection
          key={caseRow.traceId || caseRow.stageId}
          caseRow={caseRow}
          docsById={docsById}
          docsLoading={docsLoading}
          onRerun={onRerun}
          rerunningStage={rerunningStage}
          baseModel={baseModel}
          evalModel={evalModel}
        />
      ))}

      {loanCases.length === 0 && (
        <p className="mse-muted">No stage cases for this loan in the report.</p>
      )}
    </div>
  );
}
